"use client";

import { useCallback, useMemo, useRef } from "react";
import toast from "react-hot-toast";
import {
  getStudyPlan,
  getSelectableStudyPlanSessionIds,
  getStudyPlanSessionLabel,
  STUDY_PLAN_LABELS,
  STUDY_PLAN_IDS,
  type StudyPlan,
  type StudyPlanId,
} from "@/lib/study-plan";
import {
  applyToggleOverride,
  restoreTaskDynamicState,
  buildScheduledTasks,
  completionMoveApplyTargets,
  completionMoveRevertTargets,
  computeReplan,
  groupByDate,
  isDefaultOverride,
  personalStudyPlan,
  type DayGroup,
  type ExistingItemsPolicy,
  type PlanDynamicState,
  type PlanDynamicStateMap,
  type ReplanModel,
  type ScheduledTask,
  type TaskOverride,
} from "@/lib/study-plan-state";
import {
  usePlanDynamicState,
  studyStartedToast,
  subscribeAndRefetch,
  type SubscribeResult,
} from "@/hooks/usePlanDynamicState";
import { resetPlanDynamicSchedule } from "@/lib/study-plan-snapshot";
import { recoverStudyPlanRequest } from "@/lib/study-plan-request-error";
import {
  getAllPlansDynamicState,
  moveTask,
  reorderTask,
  replanStudyPlan,
  resetCompletionMovePrompt as resetCompletionMovePromptAction,
  resetStudyPlanSchedule,
  setCompletionMoveEnabled,
  setNextStudyGuideEnabled,
  setTaskSkipped,
  toggleTaskCompletion,
  unsubscribeStudyPlan,
  type CompletionMoveToggleOptions,
} from "@/lib/server-actions/studyPlanActions";

export interface SubscribedPlanMeta {
  planId: StudyPlanId;
  plan: StudyPlan;
  /** 개인 시작일(Day 1 앵커). 구독 시 사용자가 지정한 날짜, 미구독이면 정규 시작일. */
  startDateISO: string;
}

export interface ScheduleData {
  loading: boolean;
  loadError: string | null;
  /** 쓰기 요청이 하나 이상 진행 중인지 여부. 회차 전환 잠금에 사용한다. */
  writePending: boolean;
  activeExamSessionId: string;
  selectedExamSessionId: string;
  selectedExamSessionLabel: string;
  availableSessions: {
    id: string;
    label: string;
    isActive: boolean;
  }[];
  readOnly: boolean;
  selectSession: (examSessionId: string) => Promise<void>;
  /** 구독한(시작일이 설정된) 계획 메타. */
  subscribed: SubscribedPlanMeta[];
  /** 아직 시작하지 않은(미리보기 가능한) 계획 메타. */
  startable: SubscribedPlanMeta[];
  /** 구독 계획의 모든 task(날짜 앵커링·이동 반영). */
  allTasks: ScheduledTask[];
  /** 날짜별 그룹(정렬됨). */
  groups: DayGroup[];
  /** 표시 범위(min~max effectiveDate, 시험일 포함). 구독 0이면 null. */
  range: { minISO: string; maxISO: string } | null;
  /** 구독 계획 ID 집합(필터 토글 기준). */
  subscribedIds: StudyPlanId[];
  /** 낙관적 토글(완료). */
  toggle: (task: ScheduledTask, done: boolean) => Promise<void>;
  /** 낙관적 이동(다른 날짜로). toISO=null 이면 정규 날짜 복귀. */
  move: (task: ScheduledTask, toISO: string | null) => Promise<void>;
  /** 낙관적 재정렬(DnD 드롭). movedToDate=놓인 칼럼 날짜(또는 null), sortOrder=같은 날 안 위치. */
  reorder: (
    task: ScheduledTask,
    movedToDate: string | null,
    sortOrder: number | null,
  ) => Promise<void>;
  /** 낙관적 건너뛰기 토글. */
  skip: (task: ScheduledTask, skipped: boolean) => Promise<void>;
  /** 남은 미완료 task 를 오늘~시험일에 선택 모델로 재배치(낙관적, 완료 후 서버 결과로 재동기화). */
  replan: (todayISO: string, model: ReplanModel) => Promise<void>;
  /** 날짜 이동·재정렬만 정규 학습 일정으로 되돌림(완료/건너뛰기는 유지). */
  resetSchedule: (planIds: StudyPlanId[]) => Promise<void>;
  /** "완료 항목을 완료한 날짜로 자동 이동" 설정 현재 값(기본 off). (JCG-3653) */
  completionMoveEnabled: boolean;
  /** 토글 팝업의 "다시 묻지 않기" 기억값. "ask" 면 매번 묻는다. */
  completionMoveExistingPolicy: ExistingItemsPolicy;
  /**
   * 위 설정 토글(낙관적, 실패 시 롤백). options.existingItems="apply" 면 기존 완료 항목을 함께
   * 옮기거나 되돌리고, remember=true 면 그 선택을 기억한다.
   */
  setCompletionMove: (
    enabled: boolean,
    options?: CompletionMoveToggleOptions,
  ) => Promise<void>;
  /** "다시 묻지 않기" 해제 — 다음 토글부터 다시 묻는다. */
  resetCompletionMovePrompt: () => Promise<void>;
  /**
   * 계획 시작(미리보기 → 내 일정). 시작일은 항상 계획 페이지의 정규 시작일(JCG-3652)이라 결과의
   * timing(오늘이 그날보다 이른지/늦은지)으로 화면이 "오늘 기준 다시 분배" 안내를 띄운다. 실패면 null.
   */
  subscribe: (
    planId: StudyPlanId,
    todayISO: string,
  ) => Promise<SubscribeResult | null>;
  /**
   * 계획 그만두기(구독 해제). 오버라이드는 보존돼 다시 시작하면 진행이 복원된다. 그만둔 계획은
   * 일정·학습 페이지 완료 안내에서 빠진다. 성공 여부를 돌려준다. (JCG-3677)
   */
  unsubscribe: (planId: StudyPlanId) => Promise<boolean>;
  /** 학습 완료 후 다음 일정 안내 시트 on/off 현재 값(기본 on). (JCG-3677) */
  nextStudyGuideEnabled: boolean;
  /** 위 설정 토글(낙관적, 실패 시 롤백). */
  setNextStudyGuide: (enabled: boolean) => Promise<void>;
}

/**
 * 학습 일정 캘린더의 단일 데이터 소스 (JCG-2310).
 *
 * 동적 상태(getAllPlansDynamicState)를 1회 fetch 한 뒤, 정적 계획(study-plan-index.json 클라 import)과
 * 합쳐 통합 ScheduledTask[] 와 날짜 그룹을 파생한다. 모든 변경(완료·이동·건너뛰기)은 로컬 동적
 * 상태를 먼저 낙관적으로 갱신하고 서버액션 실패 시 롤백한다 — buildScheduledTasks 가 순수 함수라
 * 같은 입력으로 항상 같은 파생을 보장한다.
 *
 * enabled=false(게이트 미통과)면 fetch 를 생략한다.
 */
export function useScheduleData(enabled: boolean): ScheduleData {
  const pendingTasks = useRef(new Set<string>());
  const {
    dynState,
    setDynState,
    loading,
    sessionLoading,
    writePending,
    runPendingWrite,
    loadError,
    setLoadError,
    selectExamSession: selectSession,
    activeExamSessionId,
    selectedExamSessionId,
    readOnly,
  } = usePlanDynamicState(enabled, "학습 일정 동적 상태 로드 오류:");

  const availableSessions = useMemo(() => {
    if (!activeExamSessionId) return [];
    return getSelectableStudyPlanSessionIds(activeExamSessionId)
      .slice()
      .reverse()
      .map((id) => ({
        id,
        label: getStudyPlanSessionLabel(id),
        isActive: id === activeExamSessionId,
      }));
  }, [activeExamSessionId]);

  const subscribed = useMemo<SubscribedPlanMeta[]>(() => {
    if (!dynState || !selectedExamSessionId) return [];
    return STUDY_PLAN_IDS.flatMap((planId) => {
      const plan = getStudyPlan(planId, selectedExamSessionId);
      const dyn = dynState[planId];
      if (!plan || !dyn?.subscribed) return [];
      return [
        {
          planId,
          plan: personalStudyPlan(plan, dyn),
          startDateISO: dyn.startDateISO,
        },
      ];
    });
  }, [dynState, selectedExamSessionId]);

  const startable = useMemo<SubscribedPlanMeta[]>(() => {
    if (!dynState || !selectedExamSessionId) return [];
    return STUDY_PLAN_IDS.flatMap((planId) => {
      const plan = getStudyPlan(planId, selectedExamSessionId);
      const dyn = dynState[planId];
      if (!plan || dyn?.subscribed) return [];
      return [{ planId, plan, startDateISO: plan.startDateISO }];
    });
  }, [dynState, selectedExamSessionId]);

  const allTasks = useMemo<ScheduledTask[]>(() => {
    if (!dynState) return [];
    return subscribed.flatMap(({ planId, plan }) =>
      buildScheduledTasks(plan, dynState[planId]),
    );
  }, [dynState, subscribed]);

  const groups = useMemo<DayGroup[]>(() => groupByDate(allTasks), [allTasks]);

  const range = useMemo(() => {
    if (subscribed.length === 0) return null;
    let minISO: string | null = null;
    let maxISO: string | null = null;
    const consider = (iso: string) => {
      if (!minISO || iso < minISO) minISO = iso;
      if (!maxISO || iso > maxISO) maxISO = iso;
    };
    for (const t of allTasks) consider(t.effectiveDateISO);
    for (const { plan, startDateISO } of subscribed) {
      consider(startDateISO); // 개인 시작일(압축 후 task 가 여기서 시작)
      consider(plan.examDateISO); // 고정 시험일
    }
    if (!minISO || !maxISO) return null;
    return { minISO, maxISO };
  }, [allTasks, subscribed]);

  const subscribedIds = useMemo(
    () => subscribed.map((s) => s.planId),
    [subscribed],
  );

  // ── 낙관적 변경 ────────────────────────────────────────────────────────────

  // 같은 항목의 요청이 겹치면 실패 복구가 뒤의 성공한 변경을 덮어쓸 수 있다.
  const runTaskWrite = useCallback(async (task: ScheduledTask, work: () => Promise<void>) => {
    const key = `${task.planId}:${task.taskKey}`;
    if (pendingTasks.current.has(key)) return;
    pendingTasks.current.add(key);
    try {
      await runPendingWrite(work);
    } finally {
      pendingTasks.current.delete(key);
    }
  }, [runPendingWrite]);

  const move = useCallback(
    async (task: ScheduledTask, toISO: string | null) => {
      if (readOnly || loadError || !selectedExamSessionId || !dynState) return;
      await runTaskWrite(task, async () => {
        const before = dynState[task.planId];
        setDynState((prev) => applyMove(prev, task, toISO));
        try {
          const res = await moveTask(
            task.planId,
            task.originDayNumber,
            task.path,
            toISO,
            task.storageCodeLang ?? task.codeLang,
            selectedExamSessionId,
          );
          if (!res.success) {
            // 날짜 이동을 역으로 실행하지 않고 원래 완료 시각과 일정 조정을 복원한다.
            setDynState((prev) =>
              restoreTaskDynamicState(prev, task, before),
            );
            toast.error(
              res.needsLogin
                ? "로그인이 필요해요."
                : (res.error ?? "일정 이동에 실패했어요."),
            );
          }
        } catch (error) {
          // 요청 전 완료 시각과 일정 조정을 복원한다.
          setDynState((prev) => restoreTaskDynamicState(prev, task, before));
          if (recoverStudyPlanRequest(error, setLoadError)) return;
          toast.error("일정 이동에 실패했어요.");
        }
      });
    },
    [readOnly, loadError, dynState, runTaskWrite, selectedExamSessionId, setDynState, setLoadError],
  );

  const toggle = useCallback(
    async (task: ScheduledTask, done: boolean) => {
      if (readOnly || loadError || !selectedExamSessionId || !dynState) return;
      // 완료 항목의 자리는 완료 시각에서 파생한다(JCG-3547) — 서버도 같은 규칙이라 따로 맞출
      // 필요가 없고, 완료를 취소하면 예정 날짜로 자동 복귀하므로 되돌리기 제안도 필요 없다.
      const completedAtISO = new Date().toISOString();
      const before = dynState[task.planId];
      await runTaskWrite(task, async () => {
        setDynState((prev) =>
          applyToggleOverride(prev, task, done, completedAtISO),
        );
        try {
          const res = await toggleTaskCompletion(
            task.planId,
            task.originDayNumber,
            task.path,
            task.storageCodeLang ?? task.codeLang,
            selectedExamSessionId,
          );
          if (!res.success) {
            setDynState((prev) =>
              restoreTaskDynamicState(prev, task, before, true),
            );
            toast.error(
              res.needsLogin
                ? "로그인이 필요해요."
                : (res.error ?? "완료 처리에 실패했어요."),
            );
          } else if (typeof res.done === "boolean" && res.done !== done) {
            setDynState((prev) =>
              applyToggleOverride(prev, task, res.done!, completedAtISO),
            );
          }
        } catch (error) {
          setDynState((prev) =>
            restoreTaskDynamicState(prev, task, before, true),
          );
          if (recoverStudyPlanRequest(error, setLoadError)) return;
          toast.error("완료 처리에 실패했어요.");
        }
      });
    },
    [readOnly, loadError, dynState, runTaskWrite, selectedExamSessionId, setDynState, setLoadError],
  );

  const reorder = useCallback(
    async (
      task: ScheduledTask,
      movedToDate: string | null,
      sortOrder: number | null,
    ) => {
      if (readOnly || loadError || !selectedExamSessionId || !dynState) return;
      await runTaskWrite(task, async () => {
        const before = dynState[task.planId];
        setDynState((prev) => applyReorder(prev, task, movedToDate, sortOrder));
        try {
          const res = await reorderTask(
            task.planId,
            task.originDayNumber,
            task.path,
            movedToDate,
            sortOrder,
            task.storageCodeLang ?? task.codeLang,
            selectedExamSessionId,
          );
          if (!res.success) {
            setDynState((prev) =>
              restoreTaskDynamicState(prev, task, before),
            );
            toast.error(
              res.needsLogin
                ? "로그인이 필요해요."
                : (res.error ?? "일정 이동에 실패했어요."),
            );
          }
        } catch (error) {
          setDynState((prev) => restoreTaskDynamicState(prev, task, before));
          if (recoverStudyPlanRequest(error, setLoadError)) return;
          toast.error("일정 이동에 실패했어요.");
        }
      });
    },
    [readOnly, loadError, dynState, runTaskWrite, selectedExamSessionId, setDynState, setLoadError],
  );

  const skip = useCallback(
    async (task: ScheduledTask, skipped: boolean) => {
      if (readOnly || loadError || !selectedExamSessionId) return;
      await runTaskWrite(task, async () => {
        setDynState((prev) => applySkip(prev, task, skipped));
        try {
          const res = await setTaskSkipped(
            task.planId,
            task.originDayNumber,
            task.path,
            skipped,
            task.storageCodeLang ?? task.codeLang,
            selectedExamSessionId,
          );
          if (!res.success) {
            setDynState((prev) => applySkip(prev, task, !skipped));
            toast.error(
              res.needsLogin
                ? "로그인이 필요해요."
                : (res.error ?? "건너뛰기 처리에 실패했어요."),
            );
          }
        } catch (error) {
          setDynState((prev) => applySkip(prev, task, !skipped));
          if (recoverStudyPlanRequest(error, setLoadError)) return;
          toast.error("건너뛰기 처리에 실패했어요.");
        }
      });
    },
    [readOnly, loadError, runTaskWrite, selectedExamSessionId, setDynState, setLoadError],
  );

  const replan = useCallback(
    async (todayISO: string, model: ReplanModel) => {
      if (readOnly || loadError || !selectedExamSessionId) return;
      await runPendingWrite(async () => {
        // 낙관적: 각 구독 계획의 미완료를 오늘~시험일로 재배치(서버와 동일한 computeReplan).
        setDynState((prev) =>
          applyReplan(prev, todayISO, model, selectedExamSessionId),
        );
        let movedCount = 0;
        let blockedByExam = false;
        let ok = false;
        try {
          const res = await replanStudyPlan(
            todayISO,
            model,
            selectedExamSessionId,
          );
          ok = res.success;
          movedCount = res.movedCount ?? 0;
          blockedByExam = res.blockedByExam ?? false;
          if (!res.success) {
            toast.error(
              res.needsLogin
                ? "로그인이 필요해요."
                : (res.error ?? "다시 분배에 실패했어요."),
            );
          }
        } catch (error) {
          if (recoverStudyPlanRequest(error, setLoadError)) return;
          toast.error("다시 분배에 실패했어요.");
        }
        // 서버 결과로 최종 동기화(낙관적 계산과의 미세 차이·실패 롤백 모두 보정).
        try {
          setDynState(
            (await getAllPlansDynamicState(selectedExamSessionId)).plans,
          );
        } catch (error) {
          if (!recoverStudyPlanRequest(error, setLoadError)) {
            setLoadError("최신 일정을 확인하지 못했어요. 페이지를 새로고침해주세요.");
          }
          return;
        }
        if (ok) {
          if (movedCount > 0) {
            toast.success(
              `${movedCount}개 일정을 오늘 기준으로 다시 분배했어요.`,
            );
          } else if (blockedByExam) {
            // 시험일이 지나 분배할 날이 없는 경우(JCG-2484): 오늘에 몰지 않고 안내만.
            toast("시험일이 지나 남은 일정을 다시 분배할 수 없어요.");
          } else {
            toast.success("다시 분배할 일정이 없어요.");
          }
        }
      });
    },
    [readOnly, loadError, runPendingWrite, selectedExamSessionId, setDynState, setLoadError],
  );

  const resetSchedule = useCallback(
    async (planIds: StudyPlanId[]) => {
      if (readOnly || loadError || !selectedExamSessionId) return;
      const targetPlanIds = STUDY_PLAN_IDS.filter((planId) =>
        planIds.includes(planId),
      );
      if (targetPlanIds.length === 0) {
        toast.error("원래 감자 일정으로 복귀할 계획을 선택해주세요.");
        return;
      }

      await runPendingWrite(async () => {
        let resetCount = 0;
        let startDateResetCount = 0;
        let snapshotResetCount = 0;
        let ok = false;
        try {
          const res = await resetStudyPlanSchedule(
            targetPlanIds,
            selectedExamSessionId,
          );
          ok = res.success;
          resetCount = res.resetCount ?? 0;
          startDateResetCount = res.startDateResetCount ?? 0;
          snapshotResetCount = res.snapshotResetCount ?? 0;
          if (res.success) {
            setDynState((prev) =>
              applyScheduleReset(prev, targetPlanIds, selectedExamSessionId),
            );
          }
          if (!res.success) {
            toast.error(
              res.needsLogin
                ? "로그인이 필요해요."
                : (res.error ?? "감자 일정 복귀에 실패했어요."),
            );
          }
        } catch (error) {
          if (recoverStudyPlanRequest(error, setLoadError)) return;
          toast.error("감자 일정 복귀에 실패했어요.");
        }
        try {
          setDynState(
            (await getAllPlansDynamicState(selectedExamSessionId)).plans,
          );
        } catch (error) {
          if (!recoverStudyPlanRequest(error, setLoadError)) {
            setLoadError("최신 일정을 확인하지 못했어요. 페이지를 새로고침해주세요.");
          }
          return;
        }
        if (ok) {
          const planLabel = formatPlanLabelList(targetPlanIds);
          const successMessage =
            resetCount > 0 || startDateResetCount > 0 || snapshotResetCount > 0
              ? [
                  `${planLabel}을 원래 감자 일정으로 되돌렸어요.`,
                  "시작일·날짜 이동·순서 변경은 원본 일정 기준으로 복귀했고,",
                  "완료·건너뛰기 상태는 그대로 유지됐어요.",
                ].join(" ")
              : [
                  `${planLabel}에서 되돌릴 시작일·날짜 이동·순서 변경이 없어요.`,
                  "완료·건너뛰기 상태는 그대로 유지됐어요.",
                ].join(" ");
          toast.success(successMessage, { duration: 6000 });
        }
      });
    },
    [readOnly, loadError, runPendingWrite, selectedExamSessionId, setDynState, setLoadError],
  );

  // 사용자 단위 설정이라 계획별로 갈리지 않는다 — 아무 계획 상태에서나 같은 값을 읽는다.
  const completionMoveEnabled =
    dynState?.[STUDY_PLAN_IDS[0]]?.completionMoveEnabled === true;
  const completionMoveExistingPolicy: ExistingItemsPolicy =
    dynState?.[STUDY_PLAN_IDS[0]]?.completionMoveExistingPolicy ?? "ask";

  const nextStudyGuideEnabled =
    dynState?.[STUDY_PLAN_IDS[0]]?.nextStudyGuideEnabled !== false;

  const setCompletionMove = useCallback(
    async (enabled: boolean, options: CompletionMoveToggleOptions = {}) => {
      if (readOnly || loadError || !selectedExamSessionId) return;
      if (completionMoveEnabled === enabled && !options.remember) return;
      const existingItems = options.existingItems ?? "keep";
      const before = dynState;
      await runPendingWrite(async () => {
        // 스위치 값·기억값과(apply 면) 기존 항목의 완료 날짜를 서버와 같은 규칙으로 먼저 그린다.
        setDynState((prev) =>
          applyCompletionMove(prev, enabled, existingItems, selectedExamSessionId, {
            rememberPolicy: options.remember ? existingItems : null,
          }),
        );
        // 설정 저장과 일괄 처리는 한 트랜잭션이 아니라 실패 시 어디까지 반영됐는지 알 수 없다.
        // 그래서 실패하면 요청 전 상태로 돌리지 않고 서버를 다시 읽어 맞춘다(재조회도 실패하면 복원).
        const resync = async () => {
          try {
            setDynState(
              (await getAllPlansDynamicState(selectedExamSessionId)).plans,
            );
          } catch {
            setDynState(() => before);
          }
        };
        let ok = false;
        let affectedCount = 0;
        try {
          const res = await setCompletionMoveEnabled(
            enabled,
            options,
            selectedExamSessionId,
          );
          ok = res.success;
          affectedCount = res.affectedCount ?? 0;
          if (!res.success) {
            await resync();
            toast.error(
              res.needsLogin
                ? "로그인이 필요해요."
                : (res.error ?? "설정을 저장하지 못했어요."),
            );
          }
        } catch (error) {
          await resync();
          if (recoverStudyPlanRequest(error, setLoadError)) return;
          toast.error("설정을 저장하지 못했어요.");
        }
        if (ok && existingItems === "apply") {
          // 일괄 처리 결과(실제 대상 수)는 서버가 확정하므로 다시 읽어 맞춘다.
          try {
            setDynState(
              (await getAllPlansDynamicState(selectedExamSessionId)).plans,
            );
          } catch (error) {
            if (!recoverStudyPlanRequest(error, setLoadError))
              setLoadError(
                "최신 일정을 확인하지 못했어요. 페이지를 새로고침해주세요.",
              );
            return;
          }
        }
        if (!ok) return;
        const applied = existingItems === "apply";
        toast.success(
          enabled
            ? applied && affectedCount > 0
              ? `이미 완료한 항목 ${affectedCount}개를 완료한 날짜로 옮겼어요. 이제부터 완료하는 항목도 그날로 옮겨요.`
              : "이제부터 완료하는 항목은 완료한 날짜 칸으로 옮겨요. 이미 완료한 항목은 그대로 뒀어요."
            : applied && affectedCount > 0
              ? `옮겨둔 완료 항목 ${affectedCount}개를 예정 날짜로 되돌렸어요. 이제부터는 완료해도 예정 날짜에 그대로 둬요.`
              : "이제부터는 완료해도 예정 날짜에 그대로 둬요. 이미 옮겨둔 항목은 그대로예요.",
          { duration: 6000 },
        );
      });
    },
    [
      readOnly,
      loadError,
      dynState,
      completionMoveEnabled,
      runPendingWrite,
      selectedExamSessionId,
      setDynState,
      setLoadError,
    ],
  );

  const resetCompletionMovePrompt = useCallback(async () => {
    if (readOnly || loadError || completionMoveExistingPolicy === "ask") return;
    await runPendingWrite(async () => {
      setDynState((prev) => applyExistingPolicy(prev, "ask"));
      try {
        const res = await resetCompletionMovePromptAction();
        if (res.success) {
          toast.success("다음에 스위치를 바꿀 때 다시 물어볼게요.");
          return;
        }
        setDynState((prev) =>
          applyExistingPolicy(prev, completionMoveExistingPolicy),
        );
        toast.error(res.error ?? "설정을 저장하지 못했어요.");
      } catch (error) {
        setDynState((prev) =>
          applyExistingPolicy(prev, completionMoveExistingPolicy),
        );
        if (recoverStudyPlanRequest(error, setLoadError)) return;
        toast.error("설정을 저장하지 못했어요.");
      }
    });
  }, [
    readOnly,
    loadError,
    completionMoveExistingPolicy,
    runPendingWrite,
    setDynState,
    setLoadError,
  ]);

  const subscribe = useCallback(
    async (
      planId: StudyPlanId,
      todayISO: string,
    ): Promise<SubscribeResult | null> => {
      if (readOnly || loadError || !selectedExamSessionId) return null;
      const result = await runPendingWrite(() =>
        subscribeAndRefetch(
          setDynState,
          planId,
          selectedExamSessionId,
          setLoadError,
          todayISO,
        ),
      );
      // 시작일 안내는 ScheduleCalendar 가 다이얼로그로 하므로 토스트는 짧게.
      if (result) toast.success(...studyStartedToast(result, false));
      return result;
    },
    [
      readOnly,
      loadError,
      runPendingWrite,
      selectedExamSessionId,
      setDynState,
      setLoadError,
    ],
  );

  const unsubscribe = useCallback(
    async (planId: StudyPlanId): Promise<boolean> => {
      if (readOnly || loadError || !selectedExamSessionId) return false;
      return runPendingWrite(async () => {
        try {
          const res = await unsubscribeStudyPlan(planId, selectedExamSessionId);
          if (!res.success) {
            toast.error(res.error ?? "계획을 그만두지 못했어요.");
            return false;
          }
        } catch (error) {
          if (!recoverStudyPlanRequest(error, setLoadError))
            toast.error("계획을 그만두지 못했어요.");
          return false;
        }
        toast.success(
          `${STUDY_PLAN_LABELS[planId]} 계획을 그만뒀어요. 다시 시작하면 지금까지의 진행 기록이 복원돼요.`,
          { duration: 5000 },
        );
        try {
          setDynState(
            (await getAllPlansDynamicState(selectedExamSessionId)).plans,
          );
          setLoadError(null);
        } catch (error) {
          if (!recoverStudyPlanRequest(error, setLoadError))
            setLoadError(
              "계획은 그만뒀지만 일정을 불러오지 못했어요. 페이지를 새로고침해주세요.",
            );
        }
        return true;
      });
    },
    [
      readOnly,
      loadError,
      runPendingWrite,
      selectedExamSessionId,
      setDynState,
      setLoadError,
    ],
  );

  const setNextStudyGuide = useCallback(
    async (enabled: boolean) => {
      if (readOnly || loadError || nextStudyGuideEnabled === enabled) return;
      await runPendingWrite(async () => {
        setDynState((prev) => applyNextStudyGuide(prev, enabled));
        const rollback = () =>
          setDynState((prev) => applyNextStudyGuide(prev, !enabled));
        try {
          const res = await setNextStudyGuideEnabled(enabled);
          if (res.success) {
            toast.success(
              enabled
                ? "학습을 마치면 다음 일정을 안내할게요."
                : "학습을 마쳐도 다음 일정 안내를 띄우지 않을게요.",
            );
            return;
          }
          rollback();
          toast.error(res.error ?? "설정을 저장하지 못했어요.");
        } catch (error) {
          rollback();
          if (recoverStudyPlanRequest(error, setLoadError)) return;
          toast.error("설정을 저장하지 못했어요.");
        }
      });
    },
    [
      readOnly,
      loadError,
      nextStudyGuideEnabled,
      runPendingWrite,
      setDynState,
      setLoadError,
    ],
  );

  return {
    loading: loading || sessionLoading || !dynState,
    loadError,
    writePending,
    activeExamSessionId: activeExamSessionId ?? "",
    selectedExamSessionId: selectedExamSessionId ?? "",
    selectedExamSessionLabel: selectedExamSessionId
      ? getStudyPlanSessionLabel(selectedExamSessionId)
      : "",
    availableSessions,
    readOnly,
    selectSession,
    subscribed,
    startable,
    allTasks,
    groups,
    range,
    subscribedIds,
    toggle,
    move,
    reorder,
    skip,
    replan,
    resetSchedule,
    completionMoveEnabled,
    completionMoveExistingPolicy,
    setCompletionMove,
    resetCompletionMovePrompt,
    subscribe,
    unsubscribe,
    nextStudyGuideEnabled,
    setNextStudyGuide,
  };
}

// ── 낙관적 reducer (완료 토글은 study-plan-state.applyToggleOverride 로 공용화) ──

function applyMove(
  prev: PlanDynamicStateMap,
  task: ScheduledTask,
  toISO: string | null,
): PlanDynamicStateMap {
  if (!prev) return prev;
  const cur = prev[task.planId];
  if (!cur) return prev;

  const overrides = { ...cur.overrides };
  const existing = overrides[task.taskKey];
  // 날 단위 이동은 이전 날 기준 sortOrder 를 버린다(JCG-2483, 서버 moveTask 와 동일).
  // 완전 기본값이 되면 행 제거(서버 patchOverride 와 동일 규칙).
  const next: TaskOverride = {
    ...existing,
    status: existing?.status ?? "none",
    movedToDate: existing?.movedToDate ?? null,
    ...dateMovePatch(task, toISO),
    sortOrder: null,
  };
  if (isDefaultOverride(next)) delete overrides[task.taskKey];
  else overrides[task.taskKey] = next;

  return { ...prev, [task.planId]: { ...cur, overrides } };
}

/**
 * 서버 resolveDateMove 와 같은 규칙: 저장된 완료 날짜가 자리를 정하는 항목을 끌면 예정 날짜가
 * 아니라 완료 날짜를 그날로 다시 적는다(null = 지워서 예정 날짜로 복귀). 그 외에는 예정 날짜를
 * 옮긴다(JCG-3547, JCG-3653).
 */
function dateMovePatch(
  task: ScheduledTask,
  toISO: string | null,
): Partial<Pick<TaskOverride, "movedToDate" | "completionDate">> {
  if (task.completionDrivesDate) return { completionDate: toISO };
  return { movedToDate: toISO };
}

/** 사용자 단위 값을 모든 계획 상태에 같이 싣는 낙관적 갱신의 공통 골격. */
function mapPlans(
  prev: PlanDynamicStateMap,
  fn: (cur: PlanDynamicState, planId: StudyPlanId) => PlanDynamicState,
): PlanDynamicStateMap {
  if (!prev) return prev;
  const next = { ...prev };
  for (const planId of STUDY_PLAN_IDS)
    if (next[planId]) next[planId] = fn(next[planId], planId);
  return next;
}

/**
 * 낙관적 설정 토글(JCG-3653). 사용자 단위 설정이라 모든 계획 상태에 같은 값을 싣는다.
 * existingItems="apply" 면 서버 applyCompletionMoveToExisting 과 같은 대상 함수로 기존 완료
 * 항목의 completionDate 를 함께 적거나 지운다 — 팝업의 "N개" 와 실제 이동이 같아야 한다.
 * rememberPolicy 가 있으면 "다시 묻지 않기" 기억값도 함께 반영한다.
 */
function applyCompletionMove(
  prev: PlanDynamicStateMap,
  enabled: boolean,
  existingItems: "apply" | "keep",
  examSessionId: string,
  { rememberPolicy }: { rememberPolicy: ExistingItemsPolicy | null },
): PlanDynamicStateMap {
  return mapPlans(prev, (cur, planId) => {
    let overrides = cur.overrides;
    const plan = getStudyPlan(planId, examSessionId);
    if (existingItems === "apply" && cur.subscribed && plan) {
      overrides = { ...overrides };
      const scheduled = buildScheduledTasks(plan, cur);
      if (enabled) {
        for (const { task, completionDate } of completionMoveApplyTargets(
          scheduled,
        )) {
          const existing = overrides[task.taskKey];
          overrides[task.taskKey] = {
            ...existing,
            status: existing?.status ?? "none",
            movedToDate: existing?.movedToDate ?? null,
            completionDate,
          };
        }
      } else {
        for (const task of completionMoveRevertTargets(scheduled)) {
          const existing = overrides[task.taskKey];
          if (!existing) continue;
          const cleared: TaskOverride = { ...existing, completionDate: null };
          if (isDefaultOverride(cleared)) delete overrides[task.taskKey];
          else overrides[task.taskKey] = cleared;
        }
      }
    }
    return {
      ...cur,
      overrides,
      completionMoveEnabled: enabled,
      ...(rememberPolicy
        ? { completionMoveExistingPolicy: rememberPolicy }
        : {}),
    };
  });
}

/** "다시 묻지 않기" 기억값의 낙관적 갱신. */
function applyNextStudyGuide(
  prev: PlanDynamicStateMap,
  enabled: boolean,
): PlanDynamicStateMap {
  return mapPlans(prev, (cur) => ({ ...cur, nextStudyGuideEnabled: enabled }));
}

function applyExistingPolicy(
  prev: PlanDynamicStateMap,
  policy: ExistingItemsPolicy,
): PlanDynamicStateMap {
  return mapPlans(prev, (cur) => ({
    ...cur,
    completionMoveExistingPolicy: policy,
  }));
}

function applyReorder(
  prev: PlanDynamicStateMap,
  task: ScheduledTask,
  movedToDate: string | null,
  sortOrder: number | null,
): PlanDynamicStateMap {
  if (!prev) return prev;
  const cur = prev[task.planId];
  if (!cur) return prev;

  const overrides = { ...cur.overrides };
  const existing = overrides[task.taskKey];
  const next: TaskOverride = {
    ...existing,
    status: existing?.status ?? "none",
    movedToDate: existing?.movedToDate ?? null,
    ...dateMovePatch(task, movedToDate),
    sortOrder,
  };
  if (isDefaultOverride(next)) delete overrides[task.taskKey];
  else overrides[task.taskKey] = next;

  return { ...prev, [task.planId]: { ...cur, overrides } };
}

/** 낙관적 재분배: 각 구독 계획의 미완료에 서버와 같은 새 날짜·커리큘럼 순서를 적용한다. */
function applyReplan(
  prev: PlanDynamicStateMap,
  todayISO: string,
  model: ReplanModel,
  examSessionId: string,
): PlanDynamicStateMap {
  if (!prev) return prev;
  let next = prev;
  for (const planId of STUDY_PLAN_IDS) {
    const cur = next[planId];
    const plan = getStudyPlan(planId, examSessionId);
    if (!cur?.subscribed || !plan) continue;
    const moves = computeReplan(
      buildScheduledTasks(plan, cur),
      todayISO,
      plan.examDateISO,
      model,
    );
    if (moves.length === 0) continue;
    const overrides = { ...cur.overrides };
    for (const { task, newDateISO, newSortOrder } of moves) {
      const status = overrides[task.taskKey]?.status ?? "none";
      overrides[task.taskKey] = {
        status,
        movedToDate: newDateISO,
        sortOrder: newSortOrder,
      };
    }
    next = { ...next, [planId]: { ...cur, overrides } };
  }
  return next;
}

function applySkip(
  prev: PlanDynamicStateMap,
  task: ScheduledTask,
  skipped: boolean,
): PlanDynamicStateMap {
  if (!prev) return prev;
  const cur = prev[task.planId];
  if (!cur) return prev;

  const overrides = { ...cur.overrides };
  const existing = overrides[task.taskKey];
  const movedToDate = existing?.movedToDate ?? null;
  const sortOrder = existing?.sortOrder ?? null;
  if (skipped) {
    overrides[task.taskKey] = { status: "skipped", movedToDate, sortOrder };
  } else {
    // 해제 → status none. 이동·정렬이 없으면 행 제거.
    if (movedToDate === null && sortOrder === null)
      delete overrides[task.taskKey];
    else overrides[task.taskKey] = { status: "none", movedToDate, sortOrder };
  }

  return { ...prev, [task.planId]: { ...cur, overrides } };
}

function formatPlanLabelList(planIds: StudyPlanId[]): string {
  return planIds.map((planId) => STUDY_PLAN_LABELS[planId]).join("·");
}

function applyScheduleReset(
  prev: PlanDynamicStateMap,
  planIds: StudyPlanId[],
  examSessionId: string,
): PlanDynamicStateMap {
  if (!prev) return prev;
  let next = prev;
  for (const planId of planIds) {
    const cur = next[planId];
    const plan = getStudyPlan(planId, examSessionId);
    if (!cur?.subscribed || !plan) continue;
    next = { ...next, [planId]: resetPlanDynamicSchedule(plan, cur) };
  }
  return next;
}
